import { FieldInput } from 'components/fieldInput';
import { Form, Formik } from 'formik';
import * as Yup from "yup";
import React, { Fragment, useState, useEffect, useContext } from 'react';
import styles from '../styles.module.scss';
import axios from 'axios';
import { AuthContext, ViewContext } from 'components/lib';
import { v4 as uuidv4 } from 'uuid';
import CustomLoader from 'components/CustomLoader';
import {
  buildSchedulesFromPolicyData,
  buildSelectedPolicyDetails,
} from './selectPolicyHelpers';
import { getLatestSubmissionNumber } from 'helpers/submission';

export function SelectPolicy({
  setModal,
  setStage,
  setSelected,
  setDrivers,
  setVehicles_and_trailers,
  accountInfo
}) {
  const { user } = useContext(AuthContext);
  const context = useContext(ViewContext);
  const [companiesOptions, setCompaniesOptions] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPolicies, setLoadingPolicies] = useState(false);

  const [companyInfo, setCompanyInfo] = useState({
    companyId: "",
    companyName: "",
    companyEmail: "",
  });

  const [companyIds, setCompanyIds] = useState({});
  const [companyEmails, setCompanyEmails] = useState({});
  const [companyNames, setCompanyNames] = useState({});

  const carrierOptions = [
    { value: 'Cover Whale', label: 'Cover Whale' },
    { value: 'Progressive', label: 'Progressive' },
    { value: 'Nirvana', label: 'Nirvana' }
  ];

  const fetchCompanies = async () => {
    try {
      const response = await axios.get('/api/company/list/all', {
        params: { account_id: user.account_id }
      });

      if (response.data.success) {
        const companies = response.data.data;
        setCompaniesOptions(
          companies.map((company) => ({
            label: company.name,
            value: company.name,
          }))
        );

        const emails = companies.reduce((acc, company) => {
          acc[company.name] = company.email || '';
          return acc;
        }, {});

        const ids = companies.reduce((acc, company) => {
          acc[company.name] = company.id;
          return acc;
        }, {});

        const names = companies.reduce((acc, company) => {
          acc[company.name] = company.name;
          return acc;
        }, {});

        setCompanyEmails(emails);
        setCompanyIds(ids);
        setCompanyNames(names);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const handleCompanyChange = (company) => {
    const companyId = Array.isArray(company) ? company[0].value : company.value;
    setCompanyInfo({
      companyId: companyIds[companyId],
      companyName: companyNames[companyId],
      companyEmail: companyEmails[companyId],
    });
  };

  const fetchPolicies = async () => {
    if (!companyInfo.companyId) return;

    setLoadingPolicies(true);
    setPolicies([]);

    try {
      const response = await axios.get(
        `/api/quote/policies/all?status=active&account_id=${user.account_id}&company_id=${companyInfo.companyId}`
      );

      if (response.data.data && response.data.data.data) {
        const policiesData = response.data.data.data.map(policy => ({
          label: `${policy.company_name} - ${getLatestSubmissionNumber(policy)}`,
          value: policy.id,
          data: policy,
        }));

        setPolicies(policiesData);
      }

      setLoadingPolicies(false);
    } catch (error) {
      console.error("Error fetching policies:", error);
      setLoadingPolicies(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
    fetchPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, companyInfo.companyId]);

  const currency = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const handleSubmit = async (values) => {
    if (!values.policy) {
      console.error("No policy selected");
      return;
    }

    setLoading(true);
    try {
      // Progressive/Nirvana: no quote/info API; build selected from form, empty drivers/vehicles
      if (values.carrier === 'Progressive' || values.carrier === 'Nirvana') {
        const companyName = typeof values.company_filter === 'string'
          ? values.company_filter
          : (values.company_filter?.value ?? values.company_filter);
        const selected_policy = {
          carrier: values.carrier,
          company_name: companyName ?? '',
          submission_number: values.policy,
          company_id: null,
          insured_name: companyName ?? '',
          effective_date: new Date(),
          quote_request: values.quote_request === 'true',
          account_name: accountInfo?.name ?? '',
          agent_email: accountInfo?.owner_email ?? user?.email ?? '',
        };
        setDrivers([]);
        setVehicles_and_trailers([]);
        setSelected({
          drivers: [],
          vehicles_and_trailers: [],
          selected_policy,
        });
        setLoading(false);
        setStage('select_endorsement_type');
        return;
      }

      // Cover Whale: fetch from API and build drivers/vehicles
      const selectedPolicy = policies.find(
        (item) => item.value === values.policy
      );

      if (!selectedPolicy) {
        throw new Error("No matching policy was found for the selected value");
      }

      const selectedPolicyDetails = buildSelectedPolicyDetails({
        selectedPolicy,
        values,
        companyInfo,
        accountInfo,
        user,
      });

      let cw_data = null;

      try {
        const response = await axios.get(
          `/api/quote/info/${selectedPolicyDetails.submission_number}`
        );
        cw_data = response?.data?.data;
      } catch (error) {
        console.error("Error fetching policy details:", error);
        context.notification.show(
          'Live policy details are temporarily unavailable. Using the saved policy snapshot instead.',
          'warning',
          true,
          'toast',
          'alert-triangle'
        );
      }

      const schedules = buildSchedulesFromPolicyData({
        submissionData: cw_data,
        localQuote: selectedPolicy?.data?.quote,
        currency,
        createId: uuidv4,
      });

      setDrivers(schedules.drivers);
      setVehicles_and_trailers(schedules.vehiclesAndTrailers);

      setSelected({
        vehicles_and_trailers: schedules.vehiclesAndTrailers,
        drivers: schedules.drivers,
        selected_policy: selectedPolicyDetails,
      });

      setLoading(false);
      setStage('select_endorsement_type');
    } catch (error) {
      console.error("Error fetching policy details:", error);
      setLoading(false);
      context.notification.show(
        'Failed to load the selected policy.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  }

  return (
    <div className={styles.insideWrapper}>
      <div className={styles.title}>
        Create an Endorsement
      </div>
      <div className={styles.line} />
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <CustomLoader />
        </div>
      ) : (
        <Formik
          initialValues={{
            carrier: carrierOptions[0]?.value || "",
            policy: "",
            quote_request: "false",
            company_filter: [],
          }}
          validationSchema={Yup.object({
            carrier: Yup.string().required("Carrier is required"),
            policy: Yup.string().required("Policy is required"),
            quote_request: Yup.string().required("Please select Yes/No"),
            company_filter: Yup.mixed().required("Company is required"),
          })}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            handleChange,
            handleBlur,
            submitForm,
            isValid,
            dirty,
            setFieldValue,
          }) => (
            <Fragment>
              <Form className={styles.form} onSubmit={handleSubmit}>
                {/* Carrier Field */}
                <div className={styles.fieldRow} style={{ marginBottom: 10 }}>
                  <FieldInput
                    id="carrier"
                    type="select"
                    label="Carrier"
                    handleBlur={handleBlur}
                    showErrors
                    errors={errors.carrier}
                    touched={touched.carrier}
                    required
                    options={carrierOptions}
                    // custom onChange
                    handleChange={(e) => {
                      const newCarrier = e.target.value;
                      setFieldValue("carrier", newCarrier);

                      // reset dependent fields
                      setFieldValue("company_filter", newCarrier === "Cover Whale" ? [] : "");
                      setFieldValue("policy", "");
                    }}
                  />
                </div>

                {/* Quote Request Field */}
                <div className={styles.fieldRow} style={{ marginBottom: 10 }}>
                  <FieldInput
                    id="quote_request"
                    type="select"
                    label="Is this a quote request?"
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    showErrors
                    errors={errors.quote_request}
                    touched={touched.quote_request}
                    required
                    options={[
                      { value: "true", label: "Yes" },
                      { value: "false", label: "No" },
                    ]}
                  />
                </div>

                {/* Company Field (conditional) */}
                <div className={styles.fieldRow} style={{ marginBottom: 10 }}>
                  {values.carrier === "Cover Whale" ? (
                    <FieldInput
                      id="company_filter"
                      type="search_select"
                      label="Company Name"
                      placeholder="Select a company"
                      defaultValue={[]}
                      handleChange={handleCompanyChange}
                      options={companiesOptions}
                      required
                      errors={errors.company_filter}
                      touched={touched.company_filter}
                    />
                  ) : (
                    <FieldInput
                      id="company_filter"
                      type="text"
                      label="Company Name"
                      placeholder="Enter company name"
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      required
                      errors={errors.company_filter}
                      touched={touched.company_filter}
                    />
                  )}
                </div>

                {/* Policy Field (conditional) */}
                <div className={styles.fieldRow} style={{ marginBottom: 10 }}>
                  {values.carrier === "Cover Whale" ? (
                    <FieldInput
                      id="policy"
                      type="select"
                      label="Policy Number"
                      placeholder={
                        loadingPolicies ? "Loading Policies" : "Select a policy"
                      }
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      showErrors
                      errors={errors.policy}
                      touched={touched.policy}
                      required
                      options={policies}
                      disabled={
                        companyInfo?.companyId?.length === 0 || loadingPolicies
                      }
                    />
                  ) : (
                    <FieldInput
                      id="policy"
                      type="text"
                      label="Policy Number"
                      placeholder="Enter policy number"
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      required
                      errors={errors.policy}
                      touched={touched.policy}
                    />
                  )}
                </div>
              </Form>

              {/* Footer */}
              <div className={styles.footer} style={{ justifyContent: "flex-end" }}>
                <div className={styles.buttons}>
                  <button
                    type="button"
                    className={styles.cancel}
                    onClick={() => setModal("")}
                  >
                    Cancel
                  </button>
                  <button
                    className={styles.save}
                    onClick={submitForm}
                    disabled={!isValid || !dirty}
                  >
                    Next
                  </button>
                </div>
              </div>
            </Fragment>
          )}
        </Formik>
      )}
    </div>
  );
}
